import { FileType } from './content.constants';

export const hasFileUrlExtensions = (extensions: string[], fileUrl: string) => {
  try {
    const { pathname } = new URL(fileUrl);
    return extensions.some((extension) =>
      pathname.toLowerCase().endsWith(extension),
    );
  } catch {
    return false;
  }
};

const videoExtensions = ['.mp4', '.mov', '.ogg', '.webm'];
const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif'];
const pdfExtensions = ['.pdf'];

export const getFileTypeByName = (fileName: string): FileType => {
  if (
    videoExtensions.some((extension) =>
      fileName.toLowerCase().endsWith(extension),
    )
  ) {
    return FileType.VIDEO;
  }
  if (
    imageExtensions.some((extension) =>
      fileName.toLowerCase().endsWith(extension),
    )
  ) {
    return FileType.IMAGE;
  }
  if (
    pdfExtensions.some((extension) =>
      fileName.toLowerCase().endsWith(extension),
    )
  ) {
    return FileType.PDF;
  }
  return FileType.FILE;
};

export const setFileType = (fileUrl: string) => {
  if (hasFileUrlExtensions(videoExtensions, fileUrl)) {
    return FileType.VIDEO;
  }
  if (hasFileUrlExtensions(imageExtensions, fileUrl)) {
    return FileType.IMAGE;
  }
  if (hasFileUrlExtensions(pdfExtensions, fileUrl)) {
    return FileType.PDF;
  }
  return FileType.FILE;
};

export const isUrlFirebaseFile = (fileUrl: string) => {
  try {
    const url = new URL(fileUrl);

    return (
      url.origin.startsWith('https://firebasestorage.googleapis.com') ||
      url.origin.startsWith('https://storage.googleapis.com')
    );
  } catch {
    return false;
  }
};

export const isEmbeddedVideoUrl = (fileUrl: string): boolean => {
  const allowedEmbeddedVideoUrls = [
    'https://www.youtube.com/embed',
    'https://youtube.com/embed',
    'https://player.vimeo.com/video',
  ];

  return allowedEmbeddedVideoUrls.some((allowedEmbeddedVideoUrl) =>
    fileUrl.startsWith(allowedEmbeddedVideoUrl),
  );
};

/**
 * Converts a YouTube or Vimeo URL to its embed format.
 * Returns the embed URL if the input is a supported embed link,
 * or null if the URL is not a recognized embeddable video.
 */
export const getVideoEmbedUrl = (fileUrl: string): string | null => {
  if (!fileUrl) return null;

  // Already an embed URL
  if (isEmbeddedVideoUrl(fileUrl)) return fileUrl;

  // YouTube watch URL: youtube.com/watch?v=ID
  const youtubeWatch = fileUrl.match(
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?(?:.*&)?v=([^&\s]+)/,
  );
  if (youtubeWatch) {
    return `https://www.youtube.com/embed/${youtubeWatch[1]}`;
  }

  // YouTube short URL: youtu.be/ID
  const youtubeShort = fileUrl.match(/(?:https?:\/\/)?youtu\.be\/([^?\s]+)/);
  if (youtubeShort) {
    return `https://www.youtube.com/embed/${youtubeShort[1]}`;
  }

  // Vimeo: vimeo.com/ID
  const vimeo = fileUrl.match(/(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/);
  if (vimeo) {
    return `https://player.vimeo.com/video/${vimeo[1]}`;
  }

  return null;
};

export const humanFileSize = (size: number): string => {
  const i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
  const val = (size / 1024 ** i).toFixed(2);
  const unit = ['B', 'kB', 'MB', 'GB', 'TB'][i];

  return `${val} ${unit}`;
};
